import React, { useEffect, useState } from 'react';

import { Card, Heading, Paragraph } from '@zillow/constellation-10';

import type {
  InternalTrackingCode,
  ProratedRentErrors,
  ProratedRentRawParams,
  ProratedRentResult,
  TrackingCode,
} from './types';
import { TenantStatus } from './types';
import Form from './Form';
import Results from './Results';
import calculate from './calculate';
import validate from './validate';

// Constellation

enum CalculationState {
  INPUT_PARAMS,
  DISPLAY_RESULTS,
  ERRORED,
}

const TENANT_STATUSES = [TenantStatus.MOVE_IN, TenantStatus.MOVE_OUT];

interface ProratedRentCalculatorProps {
  itc: InternalTrackingCode;
  t: TrackingCode;
}

const ProratedRentCalculator: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<ProratedRentCalculatorProps>>
> = (props: ProratedRentCalculatorProps = { itc: '', t: '' }) => {
  const { itc, t } = props;
  const [state, setState] = useState<CalculationState>(CalculationState.INPUT_PARAMS);
  const [params, setParams] = useState<ProratedRentRawParams>({ tenantStatus: TENANT_STATUSES[0] });
  const clearParams = () => setParams({ tenantStatus: TENANT_STATUSES[0] });
  const [errors, setErrors] = useState<ProratedRentErrors>({});
  const clearErrors = () => setErrors({});
  const [proratedRent, setProratedRent] = useState<ProratedRentResult | undefined>();
  const handleCalculateButtonClick = () => {
    const validateResult = validate(params);
    if ('errors' in validateResult) {
      setErrors(validateResult.errors);
      setState(CalculationState.ERRORED);
    } else {
      clearErrors();
      const { tenantStatus, monthlyRent, moveDate } = params;
      setProratedRent(
        // typecasting is safe because params have passed validation
        calculate(tenantStatus as TenantStatus, parseFloat(monthlyRent as string), new Date(moveDate as string)),
      );
      setState(CalculationState.DISPLAY_RESULTS);
    }
  };

  const handleReset = () => {
    clearParams();
    setState(CalculationState.INPUT_PARAMS);
  };

  useEffect(() => {
    // continuously validate if user is in ERRORED state
    if (state === CalculationState.ERRORED) {
      const validationResult = validate(params);
      if ('errors' in validationResult) {
        setErrors(validationResult.errors);
      } else {
        clearErrors();
      }
    }
  }, [params, state]);

  return (
    <Card tone="neutral" elevated={true} css={{ maxWidth: '550px', minWidth: '250px' }}>
      <Heading level={1} textStyle="heading-sm" css={{ textAlign: 'center', marginBottom: '400' }}>
        Prorated rent calculator
      </Heading>
      <Paragraph css={{ textAlign: 'center', marginBottom: '600' }}>
        Calculate your tenant&apos;s prorated rent.
      </Paragraph>
      {(state === CalculationState.INPUT_PARAMS || state === CalculationState.ERRORED) && (
        <Form
          tenantStatuses={TENANT_STATUSES}
          setTenantStatus={(tenantStatus: TenantStatus) => setParams({ ...params, ...{ tenantStatus } })}
          setMonthlyRent={(monthlyRent: string) => setParams({ ...params, ...{ monthlyRent } })}
          setMoveDate={(moveDate: string) => setParams({ ...params, ...{ moveDate } })}
          handleCalculateButtonClick={handleCalculateButtonClick}
          errors={errors}
        />
      )}
      {state === CalculationState.DISPLAY_RESULTS && (
        <Results reset={handleReset} proratedRent={proratedRent as ProratedRentResult} itc={itc} t={t} />
      )}
    </Card>
  );
};

export default ProratedRentCalculator;
